import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CollaborationState, EstimateSaveRequest } from '../models/dtos';
import { EmailBase } from '../models/dtos/Classes/EmailBase';
import { CommonDataService } from './common-data.service';
import { SolEstimateService } from './estimate/sol-estimate.service';
import { CollabDialogDataService } from './common/collab-dialog-data.service';
import { ReportsCommonService } from './reports/reports-common/reports-common.service';
import { SolLevelCostCalculationService } from './operations/sol-level-cost-calculation.service';
import { CostReviewRecordBuilderService } from './review/cost-review-record-builder.service';
import { CrmOperationsService } from './crm-operations.service';
import { IndexDBService } from './common/indexDB.service';
import { CollaborationOperationsService } from './common/collaboration-operations.service';

@Injectable({
  providedIn: 'root'
})
export class EstimateCloseService implements OnDestroy {
  private readonly boundPageHideHandler: (event: PageTransitionEvent) => void;
  private isClosing = false;

  constructor(
    private readonly indexDBService: IndexDBService,
    private readonly ngZone: NgZone,
    private readonly collaborationOperationsService: CollaborationOperationsService,
    private readonly commonDataService: CommonDataService,
    private readonly solLevelCostCalculationService: SolLevelCostCalculationService,
    private readonly solEstimateService: SolEstimateService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly costReviewRecordBuilderService: CostReviewRecordBuilderService,
    private readonly collabDialogDataService: CollabDialogDataService,
    private readonly crmOperationsService: CrmOperationsService,
  ) {
    this.boundPageHideHandler = this.onPageHide.bind(this);

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('pagehide', this.boundPageHideHandler);
    });
  }

  private onPageHide(event: PageTransitionEvent): void {
    this.collaborationOperationsService.sendBeaconDiscard();

    try {
      this.indexDBService.markCurrentDatabaseForDeletion();
    } catch (error) {
      console.error('Failed to mark database for deletion (e.g., localStorage quota exceeded):', error);
    }

    this.closeEstimate()
      .catch(error => console.error('Failed to clean up on page hide:', error));
  }

  private async clearCurrentEstimateFromIndexedDB(): Promise<void> {
    if (this.isClosing) {
      console.warn('Close already in progress - skipping duplicate call.');
      return;
    }

    const currentProjectGuid = this.indexDBService.getCurrentProjectGuid();

    if (!currentProjectGuid) {
      console.warn('No current estimate to close.');
      return;
    }

    this.isClosing = true;
    try {
      await this.indexDBService.deleteDatabase();
      console.log('Current estimate closed successfully. IndexedDB database deleted.');
    } catch (error) {
      console.error('Failed to close current estimate:', error);
      throw error;
    } finally {
      this.isClosing = false;
    }
  }

  // Asks the user and performs Publish/Discard actions before closing the estimate.
  async collaborativeChecksBeforeClose(createSaveRequest: () => Promise<EstimateSaveRequest>): Promise<boolean> {
    if (this.commonDataService.getCollaborationState() === CollaborationState.Edit) {
      const result = await firstValueFrom(this.collaborationOperationsService.openPublishBeforeCloseDialog());
      if (result === 'publish') {
        return this.collabDialogDataService.publishWithNotes({
          estimateSaveRequest: await createSaveRequest(),
          collaborators: this.commonDataService.getCollaboratorData()
        });
      }
      if (result === 'discard') {
        return this.collabDialogDataService.confirmDiscardBeforeClose();
      }
      return false;
    }
    return true;
  }

  /**
   * Closes the current estimate/project and resets all estimate-scoped
   * state across every singleton service.
   *
   * Execution order:
   *  1. Delete the project-specific IndexedDB database (encrypted data).
   *  2. Reset the central data store (CommonDataService).
   *  3. Reset cached project/rate data (SolOpeningService).
   *  4. Reset the estimate UI model (SolEstimateService).
   *  5. Reset report-related caches (ReportsCommonService).
   *  6. Reset collaboration state (CollabDialogDataService).
   *  7. Reset CRM cached response (CrmOperationsService).
   *
   * User-level data (preferences, user profile, polling) is NOT cleared.
   */
  async closeEstimate(): Promise<void> {
    // 1. Clear encrypted project data from IndexedDB
    try {
      await this.clearCurrentEstimateFromIndexedDB();
    } catch (error) {
      console.error('Error closing estimate:', error);
    }

    // 2. Reset the central data store (estimate DTOs, metadata, BehaviorSubjects)
    this.commonDataService.resetEstimateState();

    // 3. Reset cached project hierarchy, rates, and cost summary data
    this.solLevelCostCalculationService.resetEstimateState();

    // 4. Reset the estimate UI model back to defaults and notify the component
    this.solEstimateService.resetEstimateModel();

    // 5. Reset report-related caches (generic names, checkboxes, multi-PDF state)
    this.reportsCommonService.resetEstimateState();

    // 6. Reset cost review data
    this.costReviewRecordBuilderService.resetCostReviewData();

    // 7. Reset collaboration published state
    this.collabDialogDataService.resetEstimateState();

    // 8. Reset CRM cached estimate response
    this.crmOperationsService.resetEstimateState();
  }

  ngOnDestroy(): void {
    window.removeEventListener('pagehide', this.boundPageHideHandler);
  }
}
